import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, todayStr, errMsg, confirmDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/Stat";
import { DataTable } from "@/components/DataTable";
import { FormDialog } from "@/components/FormDialog";
import { Search, Trash2 } from "lucide-react";

export const PROJECT_STATUSES = ["Planlanıyor", "Devam Ediyor", "Beklemede", "Tamamlandı", "İptal"];
export const PROJECT_FIELDS = [
  { name: "name", label: "Proje Adı", required: true, full: true },
  { name: "customer_id", label: "Müşteri (işveren)", type: "entity", endpoint: "/customers" },
  { name: "project_type", label: "Proje Tipi", type: "select", options: ["Konut", "Ticari", "Villa", "Ofis", "Tadilat", "Altyapı", "Diğer"] },
  { name: "address", label: "Adres", full: true },
  { name: "start_date", label: "Başlangıç", type: "date" }, { name: "planned_end_date", label: "Planlanan Bitiş", type: "date" },
  { name: "contract_amount", label: "Sözleşme Tutarı (₺, bilgi)", type: "number" },
  { name: "status", label: "Durum", type: "select", options: PROJECT_STATUSES },
  { name: "progress_percent", label: "İlerleme % (aşama yoksa)", type: "number" },
  { name: "actual_end_date", label: "Gerçek Bitiş", type: "date" },
  { name: "notes", label: "Not", type: "textarea" },
];

export default function Projects() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const load = useCallback(() => {
    api.get("/projects", { params: { q: q || undefined, status } }).then((r) => setData(r.data));
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "name", label: "Proje", render: (p) => <div><div className="font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{p.address}</div></div> },
    { key: "customer_name", label: "Müşteri" },
    { key: "status", label: "Durum", render: (p) => <StatusBadge value={p.status} /> },
    { key: "progress_percent", label: "İlerleme", render: (p) => <div className="flex items-center gap-2 min-w-[110px]"><Progress value={p.progress_percent} className="w-16" /><span className="text-xs">{p.progress_percent}%</span></div> },
    { key: "sale_count", label: "Satılan", align: "right" },
    { key: "total_sales", label: "Toplam Satış", align: "right", render: (p) => money(p.total_sales) },
    { key: "received", label: "Alınan", align: "right", render: (p) => money(p.received) },
    { key: "receivable", label: "Alınacak", align: "right", render: (p) => money(p.receivable) },
    { key: "total_cost", label: "Maliyet", align: "right", render: (p) => money(p.total_cost) },
    { key: "profit", label: "Kâr", align: "right", render: (p) => <b className={p.profit >= 0 ? "text-emerald-600" : "text-rose-600"}>{money(p.profit)}</b> },
    { key: "x", label: "", render: (p) => can("insaat") && <span onClick={(e) => e.stopPropagation()}><Button size="sm" variant="ghost" className="text-rose-600"
      onClick={() => del(p)} data-testid={`delete-project-${p.id}`}><Trash2 className="w-4 h-4 mr-1" />Sil</Button></span> },
  ];
  const del = async (p) => {
    try { if (await confirmDelete(`/projects/${p.id}`)) { toast.success("Kayıt silindi"); load(); } } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="projects-page">
      <PageHeader title="İnşaat Projeleri" subtitle="Kâr = bağımsız bölüm satışları − proje maliyetleri">
        {can("insaat") && (
          <FormDialog title="Yeni Proje" triggerLabel="Yeni Proje" triggerTestId="add-project-btn" url="/projects" onDone={load}
            successMsg="Proje oluşturuldu" initial={{ status: "Planlanıyor", project_type: "Konut", progress_percent: 0, contract_amount: 0, start_date: todayStr() }}
            fields={PROJECT_FIELDS.filter((f) => f.name !== "actual_end_date")} />
        )}
      </PageHeader>
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Proje adı veya adres..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="project-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="md:w-48" data-testid="project-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tüm Durumlar</SelectItem>{PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DataTable columns={columns} rows={data.items} onRowClick={(p) => navigate(`/projeler/${p.id}`)} testIdPrefix="project-row" />
      </Card>
    </div>
  );
}
